import { useCallback, useEffect, useState } from 'react';
import { Button, Dialog, Field, Icon, Input, useErrorDialog, useToast } from '@dayon/design-system';
import type { CatalogueChangeReason, CatalogueWriteResult } from '@dayon/contracts';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import { ReasonFields } from './ReasonFields.tsx';
import {
  catalogueRefusalTitle,
  CREATABLE_DRIVER,
  DEFAULT_CREATE_REASON,
  DRIVER_LOCK_SENTENCE,
  noteIsValid,
  percentToBasisPoints,
} from './catalogue-write.ts';

/**
 * ADD PROVIDER — `POST /api/games/providers`.
 *
 * BORN DISABLED, AND THE FORM SAYS SO BEFORE THE CLICK. There is no status
 * control here: going live is a separate, deliberately-reasoned act through
 * `POST /api/games/providers/:id/status`, which is the only kill switch a live
 * integration has. Folding it into a creation form would mean one careless
 * submit could put an uncertified integration in front of players.
 *
 * The two commercial fields are optional and EMPTY MEANS NOT RECORDED, never
 * zero — the distinction the Providers tab has drawn in a notice since before
 * either column existed.
 */
export function AddProviderDialog({
  open,
  holdsKey,
  mfa,
  onClose,
  onCreated,
}: {
  open: boolean;
  /** Holds `games.manage`. Separate from `mfa` because the refusal copy names one wall or the other. */
  holdsKey: boolean;
  mfa: boolean;
  onClose: () => void;
  onCreated: (result: CatalogueWriteResult) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [certifiedAt, setCertifiedAt] = useState('');
  const [sharePercent, setSharePercent] = useState('');
  const [reasonCode, setReasonCode] = useState<CatalogueChangeReason>(DEFAULT_CREATE_REASON);
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* Every opening starts clean. A code typed for one provider must not be
     waiting in the box for the next. */
  useEffect(() => {
    if (!open) return;
    setCode('');
    setName('');
    setCertifiedAt('');
    setSharePercent('');
    setReasonCode(DEFAULT_CREATE_REASON);
    setNote('');
    setNoteTouched(false);
  }, [open]);

  const share = percentToBasisPoints(sharePercent);
  /* THE CODE IS UPPER-CASED AS IT IS TYPED rather than validated afterwards:
     the route's regex refuses lower case because the column is UNIQUE and two
     codes differing only in case read to a human as one provider. Correcting
     the input is kinder than refusing it, and it is the same rule either way. */
  const codeValid = /^[A-Z0-9_]{2,32}$/.test(code);
  const nameValid = name.trim().length >= 2;
  const ready = codeValid && nameValid && noteIsValid(note) && share.kind !== 'invalid';

  const submit = useCallback(async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      const result = await api.post<CatalogueWriteResult>('/api/games/providers', {
        code,
        name: name.trim(),
        driver: CREATABLE_DRIVER,
        /* Absent, not null, when the box is empty: the contract distinguishes
           "not supplied" from "cleared", and a create has nothing to clear. */
        ...(certifiedAt === '' ? {} : { certifiedAt }),
        ...(share.kind === 'value' ? { revenueShareBp: share.bp } : {}),
        reasonCode,
        note: note.trim(),
      });
      toast(`${result.name} was added, disabled. Bring it live from the Providers tab when it is ready.`);
      onCreated(result);
      onClose();
    } catch (error: unknown) {
      const raw = error instanceof ApiRequestError ? error.code : undefined;
      showError({
        title: catalogueRefusalTitle(isErrorCode(raw) ? raw : undefined, name.trim() || code, {
          allowed: holdsKey,
          mfa,
        }),
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(raw ? { code: raw } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  }, [ready, code, name, certifiedAt, share, reasonCode, note, holdsKey, mfa, onCreated, onClose, showError, toast]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add a game provider"
      dismissible={!submitting}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={submitting}
            disabled={!ready || submitting}
            onClick={() => void submit()}
          >
            Add provider
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            <strong>The provider is created disabled.</strong> Nothing on its shelf can be launched
            until you bring it live from the Providers tab, which asks for its own reason and writes
            its own log row. {DRIVER_LOCK_SENTENCE}
          </div>
        </div>

        <Field
          label="Provider code"
          error={
            code !== '' && !codeValid
              ? 'Capitals, digits and underscores only, 2 to 32 characters. This code appears in URLs.'
              : undefined
          }
          hint="The provider's own short code — SA, EVO, JILI. Unique across the platform, and it cannot be changed later."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={code}
              maxLength={32}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
              placeholder="JILI"
            />
          )}
        </Field>

        <Field label="Display name" hint="What operators and players read. It can be changed by disabling and replacing the provider.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
              placeholder="JILI Games"
            />
          )}
        </Field>

        <Field
          label="Certified on"
          hint="Optional. Recorded, not enforced: no launch is refused for a missing certification date — the gate is the provider's status."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="date"
              aria-describedby={describedBy}
              value={certifiedAt}
              onChange={(e) => setCertifiedAt(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Revenue share (% of GGR)"
          error={share.kind === 'invalid' ? share.why : undefined}
          hint="Optional. Leave empty for “not recorded”, which is not the same as 0%. Stored as basis points, so two decimals."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="number"
              step="0.01"
              min="0"
              max="100"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={sharePercent}
              onChange={(e) => setSharePercent(e.target.value)}
              placeholder="12.5"
            />
          )}
        </Field>

        <ReasonFields
          reasonCode={reasonCode}
          onReasonCode={setReasonCode}
          note={note}
          onNote={setNote}
          noteTouched={noteTouched}
          onNoteBlur={() => setNoteTouched(true)}
          placeholder="Signed with JILI on 20 Sep; integration work starts next sprint."
        />
      </div>
    </Dialog>
  );
}
